<template>
  <div class="numberSourceContainer">
    <div v-if="showPatientProfilePicker">
      <PatientProfilePicker
        :ucPatientId="ucPatientId"
        :ucDoctorId="ucDoctorId"
        :businessType="5"
        @handleSelect="handleSelect"
        ref="PatientProfilePickerRef"
      />
    </div>
    <div class="date">
      {{ `${registerParams.reserveDate} ${registerParams.week} ${apmMap[registerParams.amPmId]} ` }}
    </div>
    <div class="numberContainer">
      <a-spin :indicator="indicator" v-if="loading" />
      <div
        v-else
        :class="`numberBox ${activeKey === item.numberSourceId ? 'selected' : 'unselected'}`"
        v-for="item in data"
        :key="item.numberSourceId"
        @click="() => handleClick(item)"
      >
        <div class="number">{{ item.registrationNumber }}</div>
        <div class="time">{{ item.visitTime }}</div>
      </div>
    </div>
    <div class="footer">
      <a-button type="primary" :disabled="disabled" @click="handleConfirm">确认预约</a-button>
    </div>
    <!-- 挂号信息确认 -->
    <a-modal
      :visible="verifyVisible"
      title="挂号信息确认"
      @cancel="handleVerifyCancel"
      :width="520"
      class="numberSourceContainer"
      centered
      :footer="null"
      :maskClosable="false"
    >
      <div class="verify">
        <div class="content">
          <div v-for="(item, index) in verifyContent" :key="index" class="item">
            <span class="label">{{ `${item.label}：` }}</span>
            <span class="verifyValue">{{ item.value }}</span>
          </div>
        </div>
        <a-button type="primary" @click="handleVerify">确定</a-button>
      </div>
    </a-modal>
    <!-- 患者实名认证 -->
    <RealnameAuth
      :data="props.registerParams"
      :patientProfileId="patientProfileInfo?.patientProfileId"
      :phone="phone"
      ref="RealnameAuthRef"
      @handleOk="handleRealnameAuth"
    />
  </div>
</template>

<script setup>
import { ref, defineProps, defineEmits, computed, h, defineExpose } from 'vue'
import { LoadingOutlined } from '@ant-design/icons-vue'
import RealnameAuth from './RealnameAuth.vue'
import PatientProfilePicker from '@/components/PatientProfilePicker'
import { message } from 'ant-design-vue'

const indicator = h(LoadingOutlined, {
  style: {
    fontSize: '30px'
  },
  spin: true
})
const props = defineProps({
  visible: {
    type: Boolean,
    default: false
  },
  loading: {
    type: Boolean,
    default: false
  },
  data: {
    type: Object,
    default: () => ({})
  },
  doctorInfo: {
    type: Object,
    default: () => ({})
  },
  registerParams: {
    type: Object,
    default: () => ({})
  },
  ucPatientId: {
    type: Number,
    default: 0
  },
  ucDoctorId: {
    type: Number,
    default: 0
  }
})
const emit = defineEmits(['handleCancel', 'handleConfirm'])
const apmMap = ['上午', '下午']
const PatientProfilePickerRef = ref(null)
const showPatientProfilePicker = ref(true)
const RealnameAuthRef = ref(null)
const selectNumberInfo = ref({}) // 当前选择号源数据
const activeKey = ref('') // 当前选择号源key
const verifyVisible = ref(false) // 挂号信息确认弹窗是否可视
const patientProfileInfo = ref(null) // 就诊人数据
const phone = ref('') // 就诊人手机号
const disabled = computed(() => !activeKey.value.length > 0 || !patientProfileInfo.value) // 确定预约按钮状态
const verifyContent = computed(() => [
  { label: '医生姓名', value: props.registerParams.doctorName },
  { label: '患者姓名', value: patientProfileInfo.value?.patientProfileName },
  { label: '就诊医院', value: props.registerParams.hospital },
  { label: '就诊科室', value: props.registerParams.departmentName },
  {
    label: '就诊时间',
    value: `${props.registerParams.reserveDate} ${props.registerParams.week} ${
      apmMap[props.registerParams.amPmId]
    }`
  },
  {
    label: '就诊序号',
    value: `${selectNumberInfo.value.registrationNumber}号 ${selectNumberInfo.value.visitTime}`
  }
])

// 左上角返回上一步
const handleCancel = () => {
  activeKey.value = ''
  patientProfileInfo.value = null
  PatientProfilePickerRef.value?.init()
}

// 取消挂号信息确认
const handleVerifyCancel = () => {
  verifyVisible.value = false
}

// 选择号源
const handleClick = val => {
  const { numberSourceId } = val
  selectNumberInfo.value = val
  activeKey.value = numberSourceId
}

// 确认预约
const handleConfirm = () => {
  verifyVisible.value = true
}

// 挂号信息确认
const handleVerify = async () => {
  verifyVisible.value = false
  if (!patientProfileInfo.value) {
    message.error('请选择就诊人')
    return
  }
  if (patientProfileInfo.value?.idCard === '') {
    RealnameAuthRef.value.setVisible(true)
    phone.value = patientProfileInfo.value?.phone
    return
  }
  const registrationInfo = {
    ...selectNumberInfo.value,
    patientProfileId: patientProfileInfo.value?.patientProfileId || props.ucPatientId,
    reserveDate: props.registerParams.reserveDate
  }
  emit('handleConfirm', registrationInfo)
}

// 选择就诊人
const handleSelect = item => {
  patientProfileInfo.value = item
}

// 刷新就诊人列表
const handleRealnameAuth = () => {
  PatientProfilePickerRef.value?.init()
}

defineExpose({
  handleCancel
})
</script>

<style scoped lang="scss">
.numberSourceContainer {
  .date {
    height: 12px;
    font-weight: 700;
    font-size: 16px;
    line-height: 7px;
    color: #262626;
    border-left: 3px solid #1890ff;
    padding: 3px 7px;
    margin-bottom: 16px;
  }
  .numberContainer {
    display: flex;
    flex-wrap: wrap;
    margin-bottom: 32px;
    max-height: 480px;
    overflow: scroll;
    ::v-deep(.ant-spin) {
      margin: 100px auto;
    }
    .numberBox {
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      width: 166px;
      height: 58px;
      margin: 0 18px 16px 0;
      border-radius: 4px;
      cursor: pointer;
      .number {
        font-weight: 700;
        font-size: 14px;
      }
      .time {
        font-size: 12px;
      }
    }
    .selected {
      background-color: #e2f1ff;
      border: 1.47px solid #1890ff;
      color: #1890ff;
    }
    .unselected {
      background-color: #f5f5f5;
      color: #595959;
    }
  }
  .footer {
    text-align: center;
    button {
      text-align: center;
      width: 104px;
      height: 40px;
      border-radius: 4px;
    }
    ::v-deep(.ant-btn-primary[disabled]) {
      background-color: #d9d9d9;
      border: 0;
      color: #ffffff;
    }
  }
}
.verify {
  display: flex;
  flex-direction: column;
  align-items: center;
  .content {
    text-align: left;
    margin-bottom: 5px;
    .item {
      font-size: 14px;
      font-weight: 500;
      margin-bottom: 20px;
      .label {
        color: #999999;
      }
      .verifyValue {
        color: #333333;
      }
    }
  }
  ::v-deep(.ant-btn-primary) {
    border-radius: 4px;
    background: #1890ff;
    width: 80px;
    height: 32px;
  }
}
</style>
